'use client';

import { useEffect, useRef, useState } from 'react';

// Pixel-precise block from a fixed-width design (high-fidelity handoff): the child is laid out
// at `designWidth` and scaled with a transform to the container width. `minScale` keeps a
// floor below which the block stops shrinking and scrolls horizontally instead — used for the
// 1800px charts so their labels stay legible on a phone (the page chrome around them reflows
// normally, see page.tsx).
export default function ScaledCanvas({
  children,
  designWidth = 1920,
  designHeight,
  minScale = 0,
}: {
  children: React.ReactNode;
  designWidth?: number;
  /** Fixed design height (px); measured from the content when omitted. */
  designHeight?: number;
  /** 0–1; below this the canvas scrolls sideways instead of shrinking further. */
  minScale?: number;
}) {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [height, setHeight] = useState<number | undefined>(designHeight);

  useEffect(() => {
    const update = () => {
      const w = outerRef.current?.clientWidth ?? designWidth;
      const s = Math.max(minScale, Math.min(1, w / designWidth));
      setScale(s);
      const h = designHeight ?? innerRef.current?.offsetHeight ?? 0;
      setHeight(h * s);
    };
    update();
    const ro = new ResizeObserver(update);
    if (outerRef.current) ro.observe(outerRef.current);
    if (innerRef.current) ro.observe(innerRef.current);
    return () => ro.disconnect();
  }, [designWidth, designHeight, minScale]);

  return (
    <div ref={outerRef} style={{ width: '100%', maxWidth: designWidth, margin: '0 auto', height, overflowX: 'auto', overflowY: 'hidden' }}>
      <div style={{ width: designWidth * scale, height }}>
        <div ref={innerRef} style={{ width: designWidth, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
          {children}
        </div>
      </div>
    </div>
  );
}
